import { Dialog, Transition } from '@headlessui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Divider } from '@mui/material';
import { getLastContractDay } from '@weft/services';
import type { ProductInput } from '@weft/types';
import { CONTRACT_STATUSES, NewPlanSchema, SYNC_STATUSES } from '@weft/types';
import { dayjs, isNotNullOrUndefined, withDefault } from '@weft/utils';
import { utcTimeFromDayjs } from 'apps/app/src/utils/date';
import { runMutation } from 'apps/app/src/utils/vy-mutation';
import React, { Fragment } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { z } from 'zod';
import { LocalStorageKeys } from '../../../../../constants/local-storage-keys';
import { useCreateContractWithPlan } from '../../../../../hooks/requests/use-create-contract-with-plan';
import { useCreatePlan } from '../../../../../hooks/requests/use-create-plan';
import { useVyNavigate } from '../../../../../hooks/use-vy-navigate';
import { adjustNewProductsIntervals } from '../../../../../services/products-intervals-adjuster';
import { CREATE_PLAN_MUTATION_KEY } from '../../create-plan-page-consts/create-plan-mutation-key';
import type { PlanInputForm } from '../CreatePlanForm/create-plan-form-types/plan-input-form';
import { PLAN_SAVING_OPTIONS } from './save-and-assign-as-contract-dialog-dialog-form-types/plan-saving-option';
import { SaveAndAssignAsContractDialogFormInputs } from './save-and-assign-as-contract-dialog-form-components/SaveAndAssignAsContractDialogFormInputs';

export const SaveAndAssignAsContractFormSchema = z.object({
  planSavingOption: z.nativeEnum(PLAN_SAVING_OPTIONS),
  [PLAN_SAVING_OPTIONS.AssignCustomer]: z.object({
    customerId: z.string().nonempty(),
    startDate: z.date(),
  }).optional(),
});
export type SaveAndAssignAsContractForm = z.infer<typeof SaveAndAssignAsContractFormSchema>;

type SaveAndAssignAsContractDialogFormProps = {
  open: boolean;
  onClose?: () => void;
};

export const SaveAndAssignAsContractDialogForm: React.FC<SaveAndAssignAsContractDialogFormProps> = ({
  open,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const navigate = useVyNavigate();
  const { watch } = useFormContext<PlanInputForm>();

  const form = useForm<SaveAndAssignAsContractForm>({
    resolver: zodResolver(SaveAndAssignAsContractFormSchema),
    defaultValues: {
      planSavingOption: PLAN_SAVING_OPTIONS.Template,
    },
  });

  const createContractWithPlanMutation = useCreateContractWithPlan();
  const createPlanMutation = useCreatePlan();

  function getContractEndDate(startDate: Date, duration: number) { 
    const lastContractDay = getLastContractDay(dayjs(startDate), duration);

    return lastContractDay ? utcTimeFromDayjs(lastContractDay) : undefined;
  }
  
  const handleSubmit = () => {
    return form.handleSubmit(async ({ AssignCustomer: contract, planSavingOption }) => {
      const planInput = watch();
      const fixedProducts = withDefault(planInput.fixedProducts, []);
      const usageProducts = withDefault(planInput.usageProducts, []);
      const products = [...fixedProducts, ...usageProducts];
      const adjustedProducts = products.map<ProductInput>(product => adjustNewProductsIntervals(product, planInput.billingData?.billingInterval));

      const parsedPlan = NewPlanSchema.parse({
        ...planInput,
        products: adjustedProducts,
      });

      if (planSavingOption === PLAN_SAVING_OPTIONS.Template) {
        await runMutation(
          createPlanMutation,
          parsedPlan,
          {
            pending: 'Creating plan...',
            success: 'Plan created',
            error: 'Failed to create plan',
          },
          {
            mutationKey: CREATE_PLAN_MUTATION_KEY,
            onSuccess: async ({ plan: savedPlan }) => {
              await queryClient.resetQueries(['contractPage']);
              await queryClient.resetQueries(['plan-overview']);
              localStorage.removeItem(LocalStorageKeys.CreatePlan);
              navigate(`/plans/${savedPlan.id}`);
            },
          });
      }

      if (planSavingOption === PLAN_SAVING_OPTIONS.AssignCustomer && isNotNullOrUndefined(contract)) {
        await runMutation(
          createContractWithPlanMutation,
          [{
            customerId: contract.customerId,
            startDate: contract.startDate,
            syncStatus: SYNC_STATUSES.None,
            plan: parsedPlan,
            status: CONTRACT_STATUSES.Active,
            endDate: getContractEndDate(contract.startDate, parsedPlan.billingData.duration),
          }],
          {
            pending: 'Creating contract...',
            success: 'Contract created',
            error: 'Failed to create contract',
          },
          {
            onSuccess: async ({ contracts }) => {
              const [createdContract] = contracts;

              await queryClient.invalidateQueries('contracts');
              await queryClient.invalidateQueries(['contractPage', createdContract.id]);

              localStorage.removeItem(LocalStorageKeys.CreatePlan);

              navigate(`/plans/contracts/${createdContract.id}`);
            },
          });
      }
    })();
  };

  const handleClose = () => {
    onClose?.();
    form.reset();
  };

  return (
    <>

      <Transition
        appear
        show={open}
        as={Fragment}
      >
        <Dialog
          as="div"
          className="relative z-10"
          onClose={handleClose}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/25"/>
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel
                  className="w-full max-w-2xl rounded-2xl bg-white px-14 pb-5 pt-10 text-left align-middle shadow-xl transition-all"
                >
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6"
                  >
                    Save Plan
                  </Dialog.Title>

                  <Divider className="mt-6"/>

                  <FormProvider {...form}>
                    <form>
                      <SaveAndAssignAsContractDialogFormInputs />

                      <div className="mt-4 flex justify-end gap-x-2">
                        <Button
                          variant="contained"
                          color="primary"
                          size="medium"
                          disableElevation
                          className="rounded-lg text-sm font-medium disabled:bg-[#EAEEFC] disabled:text-[#A3C8FF]"
                          onClick={handleSubmit}
                        >
                          Confirm & Create Plan
                        </Button>
                      </div>
                    </form>
                  </FormProvider>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};
